/// <amd-module name="Checks/Approvals/Web/Components/ApprovalsCheckComponent" />
import React = require("react");
import { ApprovalConfigSettings, ApprovalExecutionOrder } from "Checks/Approvals/Client/Contracts/Approval";
import { IGraphRestClient, getGraphClient } from "Checks/Approvals/Client/RestClient/Graph";
import { AccordionComponent } from "Checks/Approvals/Web/Components/AccordionComponent";
import { ControlOptions } from "Checks/Approvals/Web/Components/ControlOptions";
import { materializeApprovalIdentitiesFeatureFlag } from "Checks/Approvals/Web/Constants";
import * as Resources from "Checks/Approvals/Web/Resources";
import * as ApprovalUtilities from "Checks/Approvals/Web/Utilities";
import { CheckAction } from "Checks/Client/Contracts/Contracts";
import * as PipelineChecksContracts from "Checks/Client/Contracts/PipelinesChecks";
import { ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { PeoplePickerProvider } from "VSS/Features/Identities/IdentityPickerProvider";
import * as VS_Graph from "VSS/Platform/Contracts/Graph";
import { IdentityRef } from "VSS/Platform/Contracts/WebApi";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssComponentContext, IVssComponentProperties, VssComponent } from "VSS/Platform/Layout";
import { Checkbox } from "VSSUI/Checkbox";
import { EditableDropdown } from "VSSUI/EditableDropdown";
import { FormItem } from "VSSUI/FormItem";
import { IIdentity, IdentityPicker, IdentityType } from "VSSUI/IdentityPicker";
import { IListBoxItem, ListBoxItemType } from "VSSUI/ListBox";
import { Observer } from "VSSUI/Observer";
import { Spinner } from "VSSUI/Spinner";
import { TextField } from "VSSUI/TextField";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";

export interface IProps extends IVssComponentProperties {
    resource: PipelineChecksContracts.Resource;
    timeout: number;
    checkAction: CheckAction;
    approvers?: IdentityRef[];
    executionOrder?: ApprovalExecutionOrder;    
    checkName: string;
    checkDefinitionRefId: string;
    requesterCanBeApprover : boolean;
    instructions?: string;
    updateCheckDataAndEnableSave: (check: PipelineChecksContracts.CheckConfiguration) => void;
    updateTelemetryData: (telemetryData: { [key: string ]: string }) => void;
    disableCreateButton: (value: boolean) => void;
}

export class ApprovalsCheckComponent extends VssComponent<IProps, {}>{
    public constructor(props: IProps, context: IVssComponentContext) {
        super(props, context);
        this._pickerProvider = new PeoplePickerProvider(this.context.pageContext, {
            identityTypes: ["user", "group"],
            options: {
                MinResults: 1,
                MaxResults: 20
            }
        }, this._filterIdentities);

        this._approvalsObservable = new ObservableArray<IdentityRef>([]);
        this._minimumApproversObservable = new ObservableValue<number>(this._ALL_APPROVERS);
        this._selectedApproversObservable = new ObservableArray<IIdentity>([]);
        this._minApproversCountDropdownItems = new ObservableArray<IListBoxItem<number>>();
        this._formOverlayObservable = new ObservableValue<boolean>(false);
        this._groupApproversCount = new ObservableValue<number>(0);
        this._isMaterializationInProgress = new ObservableValue<boolean>(false);
        this._materializationErrorObservable = new ObservableValue<boolean>(false);
        this._isCheckModified = false;
        this._areControlOptionsValid = true;

        if (this.props.approvers) {
            //Overlay spinner called only in edit mode.
            this._formOverlayObservable.value = true;
            this._approvalsObservable.value = ApprovalUtilities.deepCopy<IdentityRef[]>(this.props.approvers);
            this._populateDropdownItems(this.props.approvers.length);
            if (this.props.minimumApprovers !== undefined) {
                let minApprovers = (this.props.minimumApprovers === this.props.approvers.length) ? this._ALL_APPROVERS : this.props.minimumApprovers;
                this._minimumApproversObservable.value = minApprovers;
                this._selectedMinApproverValue.select(minApprovers);
            }
            let identityLocalIDs: string[] = [];
            this.props.approvers.map((approver) => {
                identityLocalIDs.push(approver.id);
            });

            //Requesting identity service to return the entityID by passing localIDs,since the IDPicker acts on entityID.
            (this._pickerProvider.getEntitiesFromUniqueAttribute!(identityLocalIDs) as Promise<IIdentity[]>).then((detailedApproverIdentities) => {
                this._formOverlayObservable.value = false;
                //Looping done to maintain order of approvers received from the config.
                identityLocalIDs.forEach((localID) => {
                    detailedApproverIdentities.forEach((identity) => {
                        if (identity.localId === localID) {
                            this._selectedApproversObservable.push(identity);
                            if (identity.entityType.toLowerCase() === IdentityType.Group) {
                                this._groupApproversCount.value += 1;
                            }
                            return;
                        }
                    });
                });
            });
        }

        this._timeoutInMinutes = this.props.timeout;

        this._isApprovalInSequenceCheckedObservable.value = (this.props.executionOrder === ApprovalExecutionOrder.InSequence);
        this._isRequesterCanBeApproverObservable.value = this.props.requesterCanBeApprover;
        this._instructionsTextFieldObservable = new ObservableValue<string>(this.props.instructions || "");
        this._isMaterializeApprovalIdentitiesFFEnabled = isFeatureFlagEnabled(context.pageContext, materializeApprovalIdentitiesFeatureFlag);
        this._isCheckOrderingFFEnabled = isFeatureFlagEnabled(context.pageContext, "Pipelines.Checks.EnableOrderingChecks", false);
    }
    public componentDidMount(): void {
        super.componentDidMount();
        this._isMounted = true;
    }

    public componentWillUnmount(): void {
        super.componentWillUnmount();
        this._isMounted = false;
    }

    public render(): JSX.Element {
        return (
            <Observer formOverlay={this._formOverlayObservable}>
                 {(observerProps: {formOverlay: boolean}) => {
                     return (
                        <div className="approval-form">
                             {observerProps.formOverlay ?
                                <div>
                                    <div className="absolute-fill flex-column flex-center justify-center sub-layer">
                                        <Spinner>
                                            this._getFormContent();
                                        </Spinner>
                                    </div>
                                </div>
                                 : this._getFormContent()
                         }
                        </div>
                     );
                 }}
            </Observer>
        );
    }

    private _getFormContent(): JSX.Element {
        return (
            <div className="check-panel-content">
                {this._getApproversView()}
                {this._getInstructionsSection()}
                
                <AccordionComponent
                    className={"advanced-section"}
                    uniqueKey={"advancedSection"}
                    title={Resources.AdvancedSectionHeader}>
                    {this._getAdvancedSection()}
                </AccordionComponent>

                <ControlOptions
                    timeout={this.props.timeout}
                    onUpdateTimeout={this._onTimeoutUpdate}
                    setControlOptionsValidity={this._setControlOptionsValidity} />
            </div>
        );
    }

    private _getApproversView(): JSX.Element {
        return (
            <div className="approvers-section">
                <div className={"approvers-title"}> {Resources.ApproversText} </div>
                <div className="add-approvers-picker-block">
                    <Observer groupApproversCount={this._groupApproversCount} materializationInProgress={this._isMaterializationInProgress} materializationError={this._materializationErrorObservable}>
                        {(observerProps: {groupApproversCount: number, materializationInProgress: boolean, materializationError: boolean}) => {
                        return (
                            <FormItem 
                                message={this._getIdentityPickerDropdownMessage(observerProps.groupApproversCount, observerProps.materializationInProgress, observerProps.materializationError)}
                                error={observerProps.materializationInProgress || observerProps.materializationError}>
                                <IdentityPicker
                                    className="add-approvers-picker"
                                    placeholderText={Resources.PeoplePickerPlaceHolder}
                                    onIdentitiesRemoved={this._removeMultipleApprovers}
                                    onIdentityRemoved={this._removeApprover}
                                    onIdentityAdded={this._onIdentityAdded}
                                    pickerProvider={this._pickerProvider}
                                    selectedIdentities={this._selectedApproversObservable} />
                            </FormItem>
                        );
                        }}
                    </Observer>
                </div>
            </div>
        );
    }

    private _getInstructionsSection(): JSX.Element {
        return (
            <div className="approval-instructions" >
                <div className="approval-instructions-title">{Resources.InstructionsToApprovers}</div>
                <TextField
                    maxLength={4000}
                    ariaLabel={Resources.InstructionsToApprovers}
                    containerClassName="approval-instructions-text-box"
                    inputClassName="approval-instructions-textarea"
                    value={this._instructionsTextFieldObservable}
                    onChange={this._onTextFieldChange}
                    multiline
                />
            </div>
        );
    }

    private _getIdentityPickerDropdownMessage(groupApproversCount: number, isMaterializationInProgress: boolean, materializationError: boolean): string {
        let message: string;
        if (isMaterializationInProgress) {
            message = Resources.IdentityMaterializationInProgressText; 
        }
        else {
            if (materializationError) {
                message = Resources.IdentityMaterializationErrorText;
            }
            else {
                message = groupApproversCount > 0 ? Resources.GroupIdentityInfoText : "";
            }
        }

        return message;
    }

    private _getAdvancedSection(): JSX.Element {
        return (
            <Observer approvers={this._approvalsObservable} minimumApprovers={this._minimumApproversObservable}>
                {(observerProps: {approvers: IdentityRef[], minimumApprovers: number}) => {
                    return(
                        <div>
                            {observerProps.approvers.length > 1 && this._getMinApproversSelectionView()}
                            {observerProps.approvers.length > 1 && observerProps.minimumApprovers === this._ALL_APPROVERS  && this._getApproversInSequenceOptionView()}
                            {this._getRequesterCanBeApproverView()}
                        </div>
                    );
                }}
            </Observer>
        );
    }
 
    private _getMinApproversSelectionView(): JSX.Element {
        return (
            <Observer approvers={this._approvalsObservable} minimumApprovers={this._minimumApproversObservable} minApproversDropdownItems={this._minApproversCountDropdownItems}>
                {(observerProps: {approvers: IdentityRef[], minimumApprovers: number}) => {
                    return(
                        <div className="min-approvers-region">
                            <div className="min-approvers-region-header">{Resources.MinApproversSectionHeader}</div>
                            <FormItem
                                className="min-approver-error"
                                message={observerProps.minimumApprovers >= observerProps.approvers.length && Resources.MinApproverValueError}
                                error={observerProps.minimumApprovers >= observerProps.approvers.length}>
                                    <EditableDropdown
                                        className="min-approvers-region-dropdown"
                                        items={this._minApproversCountDropdownItems}
                                        onValueChange={this._onMinApproverChanged}
                                        selection={observerProps.minimumApprovers < observerProps.approvers.length ? this._selectedMinApproverValue : undefined}
                                        selectedText={observerProps.minimumApprovers < observerProps.approvers.length ? undefined : observerProps.minimumApprovers.toString()} />
                            </FormItem>
                        </div>
                    );
                }}
            </Observer>
        );
    }

    private _getApproversInSequenceOptionView(): JSX.Element {
        return (
            <div className="approval-in-sequence-region">
                
                <Checkbox
                    label={Resources.SequentialApprovalCheckBoxLabel}
                    onChange={this._onApprovalSequenceCheckChanged}
                    checked={this._isApprovalInSequenceCheckedObservable}
                />
            </div>
        );
    }

    private _getRequesterCanBeApproverView(): JSX.Element {
        return (
            <div> 
                <Checkbox
                    className="requester-approver-checkbox"
                    label={Resources.RequesterCanBeApprover}
                    onChange={this._onRequesterCanBeApproverCheckChanged}
                    checked={this._isRequesterCanBeApproverObservable}
                />
            </div>
        );
    }

    private _filterIdentities = (returnedEntities: IIdentity[]): IIdentity[] => {
        // Only materialize identities when the FF is On.Else we filter identities based on the localId
        // and need not care about materialization.

        if (!this._isMaterializeApprovalIdentitiesFFEnabled) {
            return returnedEntities && returnedEntities.filter((entitity: IIdentity) => { return entitity.localId !== null; });    
        }
        return returnedEntities;
    }

    private _onIdentityAdded = (item: IIdentity): void => {
        if (item) {
            this._materializationErrorObservable.value = false;

            if (item.localId === null) {
                this.props.disableCreateButton(true);
                this._isMaterializationInProgress.value = true;

                let materializeIdentityPromise = this._materializeApproverIdentity(item).then((localId: string) => {
                    item.localId = localId;
                    this._addApprover(item);
                }, (error: string) => {
                    this._handleMaterializationError();
                });

                if (materializeIdentityPromise) {
                    this._pendingPromises.push(materializeIdentityPromise);
                }

                Promise.all(this._pendingPromises).then(() => {
                    this._updateCheckState();
                    this._isMaterializationInProgress.value = false;
                    this._pendingPromises = [];
                }, (error: string) => {
                    this._handleMaterializationError();
                });
            }
            else {
                this._addApprover(item);
            }
        }
    }

    private _handleMaterializationError() {
        this._isMaterializationInProgress.value = false;
        this._materializationErrorObservable.value = true;
    }

    private _addApprover = (item: IIdentity): void => {
        const approvers = this._approvalsObservable.value || [];
        let addedApprover = {
            displayName: item.displayName,
            id: item.localId,
            descriptor: item.subjectDescriptor,
            imageUrl: item.image,
            uniqueName: item.mail
        } as IdentityRef;
        let approverExistsIndex = approvers.findIndex((existingApprover) => existingApprover.id === addedApprover.id);
        //Handling done to check if the approver is already present
        if (approverExistsIndex === -1) {
            this._approvalsObservable.push(addedApprover);
            this._selectedApproversObservable.push(item);
            this._handleMinApproversDropdown(this._approvalsObservable.value.length);
            if (item.entityType.toLowerCase() === IdentityType.Group) {
                this._groupApproversCount.value += 1;
            }
        }
        this._updateCheckState();
    }

    private _removeApprover = (item: IIdentity): void => {
        if (item) {
            let approvers = this._approvalsObservable.value || [];
            approvers = approvers.filter(approver => approver.id !== item.localId);
            this._handleMinApproversDropdown(approvers.length);
            if (approvers.length === 1) {
                this._minimumApproversObservable.value = this._ALL_APPROVERS;
            }
            if (item.entityType.toLowerCase() === IdentityType.Group) {
                this._groupApproversCount.value -= 1;
            }
            this._selectedApproversObservable.value = this._selectedApproversObservable.value.filter(
                (approvers: IIdentity) => approvers.localId  !== item.localId
            );
            this._approvalsObservable.value = approvers;
            this._updateCheckState();
        }
    }

    private _removeMultipleApprovers = (items: IIdentity[]): void => {
        if (items) {
            let approvers = this._approvalsObservable.value || [];
            items.forEach((item) => {
                if (item.entityType.toLowerCase() === IdentityType.Group) {
                    this._groupApproversCount.value -= 1;
                }
                approvers = approvers.filter(approver => approver.id !== item.localId);
                this._selectedApproversObservable.value = this._selectedApproversObservable.value.filter(approver => approver.localId !== item.localId);
            });
            this._approvalsObservable.value = approvers;
            this._handleMinApproversDropdown(approvers.length);
            if (approvers.length === 1) {
                this._minimumApproversObservable.value = this._ALL_APPROVERS;
            }
            this._updateCheckState();
        }
    }

    private _handleMinApproversDropdown = (approversLength: number): void => {
        this._populateDropdownItems(approversLength);
        if (this._minimumApproversObservable.value < approversLength) {
            this._selectedMinApproverValue.select(this._minimumApproversObservable.value);
        }
    } 

    private _onTextFieldChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, newValue: string): void => {
        this._instructionsTextFieldObservable.value = newValue;
        this._updateCheckState();
    }

    private _populateDropdownItems = (approversCount: number): void => {
        this._minApproversCountDropdownItems.removeAll();
        for (let num = 0; num <= approversCount - 1; num++) {
            this._minApproversCountDropdownItems.push({
                id: num.toString(),
                text: num === 0 ? Resources.SelectAllApproversAsRequired : num.toString(),
                data: num,
                type: ListBoxItemType.Row
            });
        }
    }

    private _onMinApproverChanged = (item: IListBoxItem<number>): void => {
        if (item && item.data !== undefined) {
            this._minimumApproversObservable.value = item.data;
            this._updateCheckState();
        }
    }

    private _onApprovalSequenceCheckChanged = (ev: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement> , checked: boolean): void => {
        this._isApprovalInSequenceCheckedObservable.value = checked;
        this._updateCheckState();
    }

    private _onRequesterCanBeApproverCheckChanged = (ev: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement> , checked: boolean): void => {
        this._isRequesterCanBeApproverObservable.value = checked;
        this._updateCheckState();
    }

    private _onTimeoutUpdate = (timeout: number): void => {
        this._timeoutInMinutes = timeout;
        this._updateCheckState();
    }

    private _isCheckInvalid(): boolean {
        return this._approvalsObservable.value.length === 0 || this._minimumApproversObservable.value < 0 || this._minimumApproversObservable.value >= this._approvalsObservable.value.length || !this._areControlOptionsValid;
    }

    private _updateCheckState = (): void => {
        this._isCheckModified = this._hasApproversModified() || this._hasExecutionOrderModified() || this._hasMinimumApproverModified() || this._hasInstructionsModified() || this._hasRequesterCanBeApproverModified() || this._hasTimeoutModified();
        let shouldDisableCreateButton = this._isCheckInvalid() || !(this.props.checkAction === CheckAction.Edit ? this._isCheckModified : true);
        if (shouldDisableCreateButton) {
            this.props.disableCreateButton(shouldDisableCreateButton);
        }
        else {
            let approvalConfigSettings: ApprovalConfigSettings = {
                approvers: this._approvalsObservable.value,
                executionOrder: this._isApprovalInSequenceCheckedObservable.value && this._minimumApproversObservable.value === this._ALL_APPROVERS ? ApprovalExecutionOrder.InSequence : ApprovalExecutionOrder.AnyOrder,
                instructions: this._instructionsTextFieldObservable.value,
                blockedApprovers: [],
                minRequiredApprovers: this._minimumApproversObservable.value,
                requesterCannotBeApprover: !this._isRequesterCanBeApproverObservable.value,
                definitionRef: null as any
            };

            if (this._isCheckOrderingFFEnabled) {
                approvalConfigSettings.definitionRef = {
                    id: this.props.checkDefinitionRefId
                };
            }

            let record: PipelineChecksContracts.ApprovalCheckConfiguration = {
                type: {
                    id: ApprovalUtilities.approvalPolicyTypeIdString,
                    name: ApprovalUtilities.approvalPolicyTypeName
                },
                settings: approvalConfigSettings,
                resource: this.props.resource,
                timeout: this._timeoutInMinutes
            } as PipelineChecksContracts.ApprovalCheckConfiguration;
            this.props.updateCheckDataAndEnableSave(record);
            this.props.updateTelemetryData(this._getTelemetryData(approvalConfigSettings));
        }
    }

    private _getTelemetryData = (approvalConfigSettings: ApprovalConfigSettings): { [key: string]: string } => {
        return {
            // tslint:disable-next-line:whitespace
            CheckType: ApprovalUtilities.getApprovalTypeName(approvalConfigSettings.definitionRef?.id),
            NumberOfApprovers: approvalConfigSettings.approvers.length.toString(),
            ApprovalOrder: approvalConfigSettings.executionOrder.toString(),
            MinRequiredApprovers: approvalConfigSettings.minRequiredApprovers.toString(),
            Timeout: this._timeoutInMinutes.toString()
        };
    }

    private _hasApproversModified = (): boolean => {
        if (this.props.approvers) {
            return !ApprovalUtilities.arrayEquals(this.props.approvers, this._approvalsObservable.value, (a, b) => a.id === b.id);
        }
        return false;
    }

    private _hasExecutionOrderModified = (): boolean => {
        if (this.props.executionOrder) {
            let executionOrder = this._isApprovalInSequenceCheckedObservable.value ? ApprovalExecutionOrder.InSequence : ApprovalExecutionOrder.AnyOrder;
            return this.props.executionOrder !== executionOrder;
        }
        return false;
    }

    private _hasMinimumApproverModified = (): boolean => {
        if (this.props.approvers && this.props.minimumApprovers !== undefined) {
            return !((this.props.minimumApprovers === this._minimumApproversObservable.value) || (this.props.minimumApprovers === this.props.approvers.length && this._minimumApproversObservable.value === this._ALL_APPROVERS));
        }
        return false;
    }

    private _hasInstructionsModified = (): boolean => {
        if (this.props.instructions !== undefined) {
            return this.props.instructions !== this._instructionsTextFieldObservable.value;
        }
        return false;
    }

    private _hasTimeoutModified(): boolean {
        return this.props.timeout !== this._timeoutInMinutes;
    }

    private _hasRequesterCanBeApproverModified = (): boolean => {
        return this.props.requesterCanBeApprover !== this._isRequesterCanBeApproverObservable.value;
    }
    
    private _setControlOptionsValidity = (isTimeoutValid: boolean): void => {
        this._areControlOptionsValid = isTimeoutValid;
        this._updateCheckState();
    }

    private _materializeApproverIdentity(approver: IIdentity): Promise<string> {
        const client: IGraphRestClient = getGraphClient(this.context.pageContext);
        let creationContext = null;
        if (approver.entityType.trim().toLowerCase() === "group") {
            creationContext = {
                originId: approver.originId
            } as VS_Graph.GraphGroupOriginIdCreationContext;
            return client.createGroup(creationContext).then((graphGroup: VS_Graph.GraphGroup) => {
                return client.getStorageKey(graphGroup.descriptor).then((key: VS_Graph.GraphStorageKeyResult) => {
                    return key.value;
                });
            });
        }
        else {
            creationContext = {
                originId: approver.originId
            } as VS_Graph.GraphUserOriginIdCreationContext;
            return client.createUser(creationContext).then((graphUser: VS_Graph.GraphUser) => {
                return client.getStorageKey(graphUser.descriptor).then((key: VS_Graph.GraphStorageKeyResult) => {
                    return key.value;
                }); 
            });
        } 
    }

    private _minApproversCountDropdownItems: ObservableArray<IListBoxItem<number>>;
    private _pickerProvider: PeoplePickerProvider;
    private _instructionsTextFieldObservable: ObservableValue<string>;
    private _selectedMinApproverValue: DropdownSelection = new DropdownSelection();
    private _isApprovalInSequenceCheckedObservable: ObservableValue<boolean> = new ObservableValue<boolean>(false);
    private _isRequesterCanBeApproverObservable: ObservableValue<boolean> = new ObservableValue<boolean>(false);
    private _isMounted: boolean;
    private _isMaterializeApprovalIdentitiesFFEnabled: boolean;
    private _isCheckOrderingFFEnabled: boolean;
    private _formOverlayObservable: ObservableValue<boolean>;
    private _selectedApproversObservable: ObservableArray<IIdentity>;
    private _approvalsObservable: ObservableArray<IdentityRef>;
    private _isCheckModified: boolean;
    private _minimumApproversObservable: ObservableValue<number>;
    private _groupApproversCount: ObservableValue<number>;
    private _isMaterializationInProgress: ObservableValue<boolean>;
    private _materializationErrorObservable: ObservableValue<boolean>;
    private _pendingPromises: Promise<void>[] = [];
    private _timeoutInMinutes: number;
    private _areControlOptionsValid: boolean;
    private readonly _ALL_APPROVERS: number = 0; //This indicates to include all approvers as minimum required approvers
}
